import {
  AlertError,
  InputText,
  Label,
  margins,
  PrimaryAction,
  Text,
} from '@ohgoodlord/design-system'
import { OverlayComponentProps } from '../../types'
import { Controller, useForm } from 'react-hook-form'
import { sharecodeValidationRules } from './validation'
import { css } from '@emotion/react'
import { DefaultHeader } from '../default-header'
import { ChangeEvent } from 'react'
import { DefaultFooter } from '../default-footer'
import { Model } from '../../../../types/model'
import { stripSpaces } from '../../utils'

type RightToRentModal = 'right-to-rent'

export interface RightToRentOverlayModel extends Model<RightToRentModal> {
  overlayTitle: string
  message?: string
}

export interface FormValues {
  rightToRentCode: string
}

export const RightToRentOverlay = ({
  model,
  isMobile,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  onClose,
}: OverlayComponentProps<RightToRentOverlayModel>) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    reValidateMode: 'onChange',
    defaultValues: {
      rightToRentCode: '',
    },
  })

  const onSubmit = (formData: FormValues) => {
    const rightToRentCode = stripSpaces(formData.rightToRentCode)
    onSendMessage({
      message: `Confirmed right to rent share code as ${rightToRentCode}`,
    })
    onSendEvent({
      name: 'form',
      value: { rightToRentCode },
    })
    onOverlaySubmit()
  }

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
      `}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        {renderOverlayHeader !== undefined ? (
          renderOverlayHeader()
        ) : (
          <DefaultHeader
            modal={model.modal}
            isMobile={isMobile}
            title={model.overlayTitle}
            onClose={onClose}
          />
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 15px;
            padding: 15px;
          `}
        >
          <Text>
            A Home Office assessment of your right to rent is required for your
            application.
          </Text>
          <Text>
            You can obtain a share code by visiting{' '}
            <a
              href="https://www.gov.uk/prove-right-to-rent"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.gov.uk/prove-right-to-rent
            </a>{' '}
            and following the on-screen instructions.
          </Text>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 5px;
            `}
          >
            <Label htmlFor="rightToRent">Enter your share code</Label>
            {Controller({
              name: 'rightToRentCode',
              control,
              rules: sharecodeValidationRules,
              render: ({ field: { onChange, ...field }, fieldState }) => (
                <InputText
                  {...field}
                  aria-required="true"
                  isInvalid={!!fieldState.error?.message}
                  aria-invalid={!!fieldState.error?.message}
                  aria-describedby={fieldState.error ? 'rightToRent-error' : undefined}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const transformedValue = e.target.value.toUpperCase()
                    onChange(transformedValue)
                  }}
                  name="rightToRent"
                  id="rightToRent"
                />
              ),
            })}
          </div>
          {errors.rightToRentCode && (
            <div
              id="rightToRent-error"
              css={css`
                margin-top: ${margins.s};
              `}
            >
              <AlertError>
                <Text>{errors.rightToRentCode.message}</Text>
              </AlertError>
            </div>
          )}
        </div>
      </div>
      <DefaultFooter
        renderActions={() => (
          <PrimaryAction type="submit" isFullWidth testId="modal-primary-action-button">
            Done
          </PrimaryAction>
        )}
      />
    </form>
  )
}
